import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize, Subject, takeUntil } from 'rxjs';
import { CoachCalendarService } from '../../../core/services/coach-calendar.service';
import { IAppointment, IAvailabilitySlot } from '../../../core/models/appointment.model';
import { EAppointmentStatus } from '../../../core/enums/appointment-status.enum';
import { minutesOfDay, minutesToTime, timeToMinutes, tz } from '../../../core/helpers/calendar.helper';

interface IDayRow {
  dayOfWeek: number;
  titleKey: string;
  slots: { startTime: string, endTime: string }[];
}

/** Monday first for the editor, while the API keeps Sunday as 0. */
const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const DAY_TITLE_KEYS = [
  'agenda.days.sunday',
  'agenda.days.monday',
  'agenda.days.tuesday',
  'agenda.days.wednesday',
  'agenda.days.thursday',
  'agenda.days.friday',
  'agenda.days.saturday',
];
const TIME_OPTION_STEP_MINUTES = 15;
const MAX_SLOTS = 50;

@Component({
  selector: 'coach-availability-modal',
  templateUrl: './availability-modal.component.html',
  styleUrl: './availability-modal.component.scss',
})
export class AvailabilityModalComponent implements OnInit, OnDestroy {
  @Input() slots: IAvailabilitySlot[] = [];
  @Input() appointments: IAppointment[] = [];

  days: IDayRow[] = [];
  timeOptions: { id: string, name: string }[] = [];

  isLoading = false;
  errKey?: string;

  destroyed$ = new Subject();

  constructor(
    public modal: NgbActiveModal,
    private calendarService: CoachCalendarService,
  ) {
  }

  ngOnInit(): void {
    this.timeOptions = this.buildTimeOptions();
    this.days = DAY_ORDER.map(dayOfWeek => ({
      dayOfWeek,
      titleKey: DAY_TITLE_KEYS[dayOfWeek],
      slots: this.slots
        .filter(slot => slot.dayOfWeek === dayOfWeek)
        .map(slot => ({startTime: slot.startTime.slice(0, 5), endTime: slot.endTime.slice(0, 5)}))
        .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime)),
    }));
  }

  totalSlots(): number {
    return this.days.reduce((total, day) => total + day.slots.length, 0);
  }

  canSave(): boolean {
    return !this.isLoading && !this.validationErrorKey() && (this.totalSlots() <= MAX_SLOTS);
  }

  /**
   * Meetings that would fall outside the edited availability. The API does not cancel them, so the
   * coach is told before saving instead of finding them stranded on the grid afterwards.
   */
  strandedAppointments(): IAppointment[] {
    return this.appointments
      .filter(a => a.status !== EAppointmentStatus.Cancelled)
      .filter(a => tz(a.startsAt).isAfter(tz()))
      .filter(appointment => {
        const day = this.days.find(d => d.dayOfWeek === tz(appointment.startsAt).day());
        if (!day) {
          return true;
        }

        const start = minutesOfDay(appointment.startsAt);
        const end = minutesOfDay(appointment.endsAt);
        return !day.slots.some(slot => (start >= timeToMinutes(slot.startTime)) && (end <= timeToMinutes(slot.endTime)));
      });
  }

  addSlot(day: IDayRow) {
    day.slots.push({startTime: '09:00', endTime: '12:00'});
  }

  removeSlot(day: IDayRow, index: number) {
    day.slots.splice(index, 1);
  }

  validationErrorKey(): string | undefined {
    for (const day of this.days) {
      const sorted = [...day.slots].sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

      for (let i = 0; i < sorted.length; i++) {
        if (timeToMinutes(sorted[i].startTime) >= timeToMinutes(sorted[i].endTime)) {
          return 'agenda.errors.invalid_range';
        }
        if (i > 0 && (timeToMinutes(sorted[i].startTime) < timeToMinutes(sorted[i - 1].endTime))) {
          return 'agenda.errors.overlapping_range';
        }
      }
    }

    return (this.totalSlots() > MAX_SLOTS) ? 'agenda.errors.too_many_slots' : undefined;
  }

  onSubmit() {
    this.errKey = this.validationErrorKey();
    if (this.errKey) {
      return;
    }

    // The API replaces every slot, so the whole week travels with each save.
    const payload: IAvailabilitySlot[] = this.days.flatMap(day =>
      day.slots.map(slot => ({dayOfWeek: day.dayOfWeek, startTime: slot.startTime, endTime: slot.endTime}))
    );

    this.isLoading = true;
    this.calendarService.setAvailability(payload)
      .pipe(takeUntil(this.destroyed$), finalize(() => this.isLoading = false))
      .subscribe(() => {
        this.modal.close(true);
      }, () => {
        this.errKey = 'agenda.errors.availability_failed';
      });
  }

  private buildTimeOptions(): { id: string, name: string }[] {
    const options: { id: string, name: string }[] = [];
    for (let minutes = 0; minutes < (24 * 60); minutes += TIME_OPTION_STEP_MINUTES) {
      const label = minutesToTime(minutes);
      options.push({id: label, name: label});
    }

    return options;
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
